import EncryptedStorage from 'core/utils/constants/encryptedStorage';
import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'controller/utils/@reduxjs/toolkit';
import {
  useInjectReducer,
  useInjectSaga,
} from 'controller/utils/redux-injectors';
import History from 'core/utils/history';
import CustomResponse from 'core/utils/types/CustomResponse';
import { AcademicYear } from 'core/utils/types/modules/org';

import { authenticationSaga } from './saga';
import { AuthenticationState, JWTPayload, StaffRoleAuth } from './types';
import { applyAccessDataToAuth } from 'core/utils/accessControl';

export const initialState: AuthenticationState = {
  isLoggingIn: false,
  authData: {
    IS_LOGGED_IN: false,
  } as JWTPayload,
  errorLoggingIn: undefined,
  isLoggingOut: false,
  errorLogout: undefined,
  scrollPosition: 0,
  sessionLimitError: undefined,
};

const slice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    loggingInStart(
      state,
      action: PayloadAction<{
        data: { USER_NAME: string; PASSWORD: string; SOURCE: string; DEVICE_TYPE?: string; LOCATION?: string; FORCE_LOGIN_ID?: number[] };
        cancelToken: any;
      }>,
    ) {
      state.isLoggingIn = true;
      state.sessionLimitError = undefined;
    },
    // A session minted elsewhere, handed over by the onboarding wizard.
    sessionHandoffStart(state, action: PayloadAction<{ session: any }>) {
      state.isLoggingIn = true;
      state.sessionLimitError = undefined;
    },
    loggingInSuccess(state, action: PayloadAction<JWTPayload | any>) {
      state.isLoggingIn = false;
      state.authData = action.payload;
      if (action?.payload?.STAFF_INFO?.length === 1) {
        EncryptedStorage.setItem('_DATA_', JSON.stringify(action.payload));
      }
    },
    loggingInFailure(state, action: PayloadAction<CustomResponse | undefined>) {
      state.isLoggingIn = false;
      state.errorLoggingIn = action.payload;
    },
    selectRole(state, action: PayloadAction<StaffRoleAuth>) {
      state.authData.STAFF_ROLE = action.payload;
      EncryptedStorage.setItem('_DATA_', JSON.stringify(state.authData));
    },
    selectModules(state, action: PayloadAction<any>) {
      if (action?.payload?.registry && action?.payload?.accessData) {
        state.authData = applyAccessDataToAuth(state.authData, action.payload.registry, action.payload.accessData);
        EncryptedStorage.setItem('_DATA_', JSON.stringify(state.authData));
        return;
      }
    },
    updateAcademicYearListOnUpsert(state, action: PayloadAction<AcademicYear>) {
      const academicYear = state.authData.ACADEMIC_YEARS?.find(item => Boolean(+item?.ACADEMIC_YEAR_ID === +action.payload?.ACADEMIC_YEAR_ID));
      if (!!academicYear) {
        state.authData.ACADEMIC_YEARS = [...state.authData.ACADEMIC_YEARS]?.map(item => Boolean(+item?.ACADEMIC_YEAR_ID === +action.payload?.ACADEMIC_YEAR_ID) ? action.payload : item);
      } else {
        state.authData.ACADEMIC_YEARS = [...state.authData.ACADEMIC_YEARS, action.payload];
      }
      EncryptedStorage.setItem('_DATA_', JSON.stringify(state.authData));
    },
    setAcademicYear(state, action: PayloadAction<AcademicYear>) {
      state.authData.SELECTED_ACADEMIC_YEAR = action.payload;
      EncryptedStorage.setItem('_DATA_', JSON.stringify(state.authData));
      EncryptedStorage.setItem(
        '_SELECTED_ACADEMIC_YEAR_',
        JSON.stringify(state.authData.SELECTED_ACADEMIC_YEAR),
      );
    },
    updateAcademicYearList(state, action: any) {
      const data = {
        ...action.payload?.DATA,
        ACADEMIC_YEARS: action.payload?.list,
      };
      const updatedSelectedAcademicYear =
        action.payload?.list?.find(
          ({ ACADEMIC_YEAR_ID }) =>
            ACADEMIC_YEAR_ID ===
            action.payload?.SELECTED_ACADEMIC_YEAR?.ACADEMIC_YEAR_ID,
        ) || action.payload?.list?.[0];
      state.authData.ACADEMIC_YEARS = action.payload?.list;
      state.authData.SELECTED_ACADEMIC_YEAR = updatedSelectedAcademicYear;
      EncryptedStorage.setItem(
        '_SELECTED_ACADEMIC_YEAR_',
        JSON.stringify(updatedSelectedAcademicYear),
      );
      EncryptedStorage.setItem('_DATA_', JSON.stringify(data));
    },
    setBranchID(state, action: PayloadAction<number>) {
      state.authData.BRANCH_ID = action.payload;
      EncryptedStorage.setItem('_DATA_', JSON.stringify(state.authData));
      EncryptedStorage.setItem(
        '_BRANCH_ID_',
        JSON.stringify(state.authData.BRANCH_ID),
      );
    },
    setBranchIDAndAcademicYearAlso(state, action: PayloadAction<JWTPayload | any>) {
      state.authData = action.payload;
      EncryptedStorage.setItem('_DATA_', JSON.stringify(state.authData));
      EncryptedStorage.setItem('_BRANCH_ID_', JSON.stringify(state.authData.BRANCH_ID));
    },
    logoutStart(state, action: PayloadAction<{ USER_LOGIN_ID: number, cancelToken: any, isCancelFlow?: boolean }>) {
      state.isLoggingOut = true;
    },
    logoutSuccess(state, action: PayloadAction<{ isCancelFlow?: boolean }>) {
      state.isLoggingOut = false;
      state.authData = { IS_LOGGED_IN: false } as JWTPayload;
      sessionStorage.removeItem('hasRedirectedToNotifications');
      // History.navigate('/');
      if (!action.payload?.isCancelFlow) {
        window.location.href = "/";
      }
    },
    logoutFailure(state, action: PayloadAction<any>) {
      state.isLoggingOut = false;
      state.authData = { IS_LOGGED_IN: false } as JWTPayload;
      state.errorLogout = action.payload;
      if (!action.payload?.isCancelFlow) {
        window.location.href = "/";
      }
    },
    clearState(state) {
      state.isLoggingIn = false;
      state.authData = {
        IS_LOGGED_IN: false,
      } as JWTPayload;
      state.errorLoggingIn = undefined;
      state.isLoggingOut = false;
      state.errorLogout = undefined;
      state.sessionLimitError = undefined;
    },
    sessionLimitReached(state, action: PayloadAction<any>) {
      state.isLoggingIn = false;
      state.sessionLimitError = action.payload;
    },
    clearSessionLimitError(state) {
      state.sessionLimitError = undefined;
    },
    updateAuthData(state, action: PayloadAction<Partial<JWTPayload>>) {
      state.authData = {
        ...state.authData,
        ...action.payload,
      };
      EncryptedStorage.setItem('_DATA_', JSON.stringify(state.authData));
    },
    updateAuthDataOnInstituteSelection(state, action: PayloadAction<Partial<JWTPayload>>) {
      state.authData = action.payload as any;
      if (action.payload.ACCESS_TOKEN) {
        EncryptedStorage.setItem('_UFT_', `Bearer ${action.payload.ACCESS_TOKEN}`);
      }
      if (action.payload.DATABASE) {
        EncryptedStorage.setItem('_DATABASE_', String(action.payload.DATABASE) as string);
      }
      EncryptedStorage.setItem('_DATA_', JSON.stringify(action.payload));
    },
    setColumnsRole(state, action: PayloadAction<Partial<JWTPayload>>) {
      state.authData.COLUMNS = action.payload as any;
      EncryptedStorage.setItem('_DATA_', JSON.stringify(state.authData));
    },
    setScrollPosition(state, action: PayloadAction<number>) {
      state.scrollPosition = action.payload;
    },
    clearScrollPosition(state) {
      state.scrollPosition = 0;
    }
  },
});

export const { actions: authenticationActions } = slice;

export const useAuthenticationSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: authenticationSaga });
  return { actions: slice.actions };
};

